// =======================================================
// File: src/components/Timers/TimeLogsCharts.jsx
// Drop-in
// Notes:
// - Keeps Recharts usage, adds guards and consistent rounding
// - Leaves colors to theme defaults; no custom palettes
// =======================================================
import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
} from "recharts";

export default function TimeLogsCharts({ logs = [], staffList = [], stepLabel }) {
  const byStaff = useMemo(() => {
    const map = {};
    (logs || []).forEach((L) => {
      const k = String(L?.staffId ?? "unassigned");
      map[k] = map[k] || { staffId: k, minutes: 0 };
      map[k].minutes += Number(L?.minutes) || 0;
    });
    return Object.entries(map)
      .map(([id, v]) => ({
        staff: staffList.find((s) => String(s.id) === String(id))?.name || "Unassigned",
        minutes: +Number(v.minutes || 0).toFixed(1),
      }))
      .sort((a, b) => b.minutes - a.minutes);
  }, [logs, staffList]);

  const byDay = useMemo(() => {
    const map = {};
    (logs || []).forEach((L) => {
      const d = new Date((L?.finishedAt ?? L?.startedAt) ?? 0);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;
      map[key] = (map[key] || 0) + (Number(L?.minutes) || 0);
    });
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, minutes]) => ({ date, minutes: +Number(minutes || 0).toFixed(1) }));
  }, [logs]);

  const byStep = useMemo(() => {
    const map = {};
    (logs || []).forEach((L) => {
      const k = L?.step || "unknown";
      map[k] = (map[k] || 0) + (Number(L?.minutes) || 0);
    });
    return Object.entries(map).map(([k, minutes]) => ({
      step: typeof stepLabel === "function" ? stepLabel(k) : k,
      minutes: +Number(minutes || 0).toFixed(1),
    }));
  }, [logs, stepLabel]);

  if (!logs?.length) return null;

  return (
    <div className="grid xl:grid-cols-3 gap-4">
      <ChartCard title="Minutes by Staff">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={byStaff} margin={{ left: 12, right: 12 }}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="staff" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="minutes" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Minutes per Day">
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={byDay} margin={{ left: 12, right: 12 }}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey="minutes" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Step Distribution">
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Tooltip />
            <Legend />
            <Pie data={byStep} dataKey="minutes" nameKey="step" outerRadius={100} label />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="rounded-2xl border p-3">
      <div className="font-semibold mb-2">{title}</div>
      {children}
    </div>
  );
}
